import { motion, useInView, useAnimate, stagger } from 'framer-motion';
import { useContext, useEffect } from 'react';
import { PageContext } from '../../contexts';
import { useInViewScroll, useSanitizedTranslation } from '../../hooks';
import { CTA, IImage } from '../../types';
import { IFormField, XumoForm } from 'ui';
import { useScrolledTheme } from 'ui';
import Image from 'next/image';

export interface BusinessFormProps {
  key?: string;
  heading: string;
  body: string;
  formFields: IFormField[];
  CTA: CTA;
  backgroundColor?: string;
  bottomBackgroundColor?: string;
  backgroundImage?: IImage;
  headingOne?: boolean;
  styleOverrides?: {
    form?: string;
    formField?: string;
    CTA?: {
      general?: string;
      disabledColor?: string;
      enabledColor?: string;
      alignment?: string;
    };
    hideLabel?: boolean;
  };
}

const squiggleVariant = {
  hidden: {
    opacity: 0,
    scale: 1.05,
    transition: {
      when: 'afterChildren',
      delayChildren: 0,
      staggerChildren: 0.025,
      type: 'spring',
      stiffness: 400,
      damping: 40,
    },
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      staggerChildren: 0.055,
      type: 'spring',
      stiffness: 90,
      damping: 23,
      restDelta: 0.001,
    },
  },
};

export function BusinessForm({ CTA, heading, body, formFields, backgroundColor, headingOne, styleOverrides }: BusinessFormProps) {
  const { ref, progress: y } = useInViewScroll([200, 0], ['start 100vh', 'end 0vh']);
  const { pageCtx } = useContext(PageContext);
  const { t } = useSanitizedTranslation();

  const { host } = pageCtx;

  const HeadingComponent = headingOne ? motion.h1 : motion.h2;

  const { componentInView } = useScrolledTheme({
    ref,
    background: backgroundColor,
    // TODO: add accent to directus
    accent: '#000000',
    text: '#000000',
  });

  const [viewref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        '.formwrapper',
        {
          y: [50, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 0.9,
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  return (
    <>
      <div ref={ref} className="relative z-40 grid w-full grid-cols-1 grid-rows-1 items-center justify-center bg-[#EFF3F5] py-16">
        {/* Background */}
        <Image
          src="/static/images/refresh/2025/form-bg.png"
          alt="icon"
          width={1853}
          height={1522}
          className='col-start-1 row-start-1 w-full h-auto hidden lg:block max-w-[1500px] mx-auto translate-x-20'
        />
        {/* Form */}
        <section className="wrapper relative w-full col-start-1 row-start-1 z-0" data-testid={'lead-form-section'}>
        {/* Anchor */}
        <div id="contact" className='absolute -top-[15vh]'></div>
        {/* Dots on top */}
        <Image
          src='/static/images/refresh/2025/form-right.png' 
          alt="icon"
          width={260} 
          height={246}
          className='absolute w-[200px] z-30 -top-10 hidden lg:block right-0 pointer-events-none'
        />
        {/* Dots on bottom */}
        <Image
          src='/static/images/refresh/2025/form-left.png' 
          alt="icon"
          width={304} 
          height={530}
          className='absolute w-[200px] z-30 -bottom-10 hidden lg:block left-0 pointer-events-none'
        />
        {/* Form wrapper */}
          <div className="relative flex w-full flex-col items-center lg:px-28" ref={viewref}>
            <div
              className="form.modal content-wrapper grow-1 z-20 flex w-full flex-col items-center
        justify-center rounded-lg bg-white formwrapper"
            >
              <HeadingComponent
                className="type-heading mb-8 px-12 text-center md:mb-0 md:px-0"
                data-i18n={heading}
                dangerouslySetInnerHTML={{ __html: t(heading) }}
              />
              <div className="type-body allow-html p-6 text-center lg:w-1/2" data-i18n={body} dangerouslySetInnerHTML={{ __html: t(body) }} />
              <div className="w-full lg:w-2/3">
                <XumoForm
                  formFields={formFields}
                  CTA={CTA}
                  formAction={{ submissionEndpoint: '/api/contact', successMessage: '' }}
                  styleOverrides={{
                    CTA: {
                      general: `text-white border-transparent py-2 px-12`,
                      disabledColor: 'bg-xumoAltBerryDark',
                      enabledColor: 'bg-xumoAltBerryLight',
                    },
                    formField: `w-full`,
                    form: 'w-full justify-center items-center px-8 sm:px-16 sm:pt-8 gap-4',
                    ...styleOverrides,
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
